import { camelCase, flatMapDeep, isPlainObject, isString } from "lodash";

/** Is Null Or Empty String
 *
 * This helper function checks if a string is null, undefined, or empty.
 * This differs from a simple falsy check as it specifically targets strings
 * and trims them to check for strings that are only whitespace.
 *
 * @param text - The string to check
 *
 * @returns - True if the string is null, undefined, or empty; otherwise false
 */
export function isNullOrEmptyString(
  text: string | null | undefined,
): text is null | undefined | "" {
  return (
    text === undefined ||
    text === null ||
    (isString(text) && text.trim().length === 0)
  );
}

/** To Pascal Case
 *
 * This helper function converts a string to PascalCase.
 *
 * @param str - The string to convert
 *
 * @returns - The PascalCase version of the string
 */
export const toPascalCase = (str: string): string =>
  camelCase(str).replace(/^[a-z]/, (c) => c.toUpperCase());

/** Extract Strings Deep
 *
 * This helper function extracts all strings from an object recursively
 * and returns them as a flat array.
 *
 * @param obj - The object to extract strings from
 *
 * @returns - An array containing all strings found within the object
 */
export const extractStringsDeep = (obj: object): string[] => {
  if (!isPlainObject(obj)) return [];
  return flatMapDeep(obj, (value) =>
    isString(value) ? [value] : extractStringsDeep(value),
  );
};
